import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import LoadingIcon from 'studio_react/common/components/LoadingIcon';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';
import { useClientEventApiFetch, useEvent } from 'ts/client/common';
import { getBaseUrl } from 'ts/client/common/hooks/useClientApiFetch';
import {
    ISlideshowPlayerTickerHandle,
    SlideshowPlayerTicker,
    buildOverlapTimeline,
    getBoundaryTransitionDurations,
    getSlideDurationMs
} from 'ts/common/components/slideshows';
import { getWatermarkCanvasDimensions } from 'ts/common/components/slideshows/slideshowWatermarkUtils';
import SlideshowControls from './SlideshowPlayer/SlideshowControls';

interface ISpAuthorizationToken {
    getAuthorizationTokenForEvent: (
        forceRefresh?: boolean,
        request?: { albumId?: number | string }
    ) => Promise<string>;
}

interface Props {
    photoId: number;
    albumId?: number | string | null;
    spAuthorizationToken: ISpAuthorizationToken;
}

const ClientSlideshowViewer: FC<Props> = ({ photoId, albumId, spAuthorizationToken }) => {
    const { brandId } = useEvent();
    const { response, loading, performFetch } =
        useClientEventApiFetch<SpApi.Client.IEventSlideshow>(`photo/${photoId}`, { defer: true });

    const [isPlaying, setIsPlaying] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [currentTimeMs, setCurrentTimeMs] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const tickerRef = useRef<ISlideshowPlayerTickerHandle>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        const tokenRequest =
            albumId !== null && albumId !== '' && albumId !== 'all' ? { albumId } : undefined;

        (async () => {
            const token = await spAuthorizationToken.getAuthorizationTokenForEvent(
                false,
                tokenRequest
            );

            if (cancelled) {
                return;
            }

            const result = await performFetch({ headers: { Authorization: `Bearer ${token}` } });

            if (cancelled) {
                return;
            }

            if (result?.status === 403) {
                const freshToken = await spAuthorizationToken.getAuthorizationTokenForEvent(
                    true,
                    tokenRequest
                );

                if (!cancelled) {
                    performFetch({ headers: { Authorization: `Bearer ${freshToken}` } });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [photoId, albumId, spAuthorizationToken, performFetch]);

    // Reset playback state whenever the slideshow changes
    useEffect(() => {
        if (response?.type === 'event-slideshow' && response.slides.length > 0) {
            setCurrentTimeMs(0);
            setIsPlaying(false);
            setHasStarted(false);
        }
    }, [response]);

    // Track fullscreen state
    useEffect(() => {
        const onFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener('fullscreenchange', onFullscreenChange);

        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    // Total duration in milliseconds, using the same overlap logic as the player
    const totalDurationMs = useMemo(() => {
        if (response?.type !== 'event-slideshow' || response.slides.length === 0) {
            return 0;
        }

        const segDurations = response.slides.map(getSlideDurationMs);
        const overlaps = getBoundaryTransitionDurations(response);

        return buildOverlapTimeline(segDurations, overlaps).totalDuration;
    }, [response]);

    const watermarkPath = useMemo(() => {
        const wid = response?.brandWatermarkId;

        if (!wid || !brandId) {
            return undefined;
        }

        const { width, height } = getWatermarkCanvasDimensions(response?.aspectRatio);

        return `/brand/${brandId}/watermark/${wid}?format=png&width=${width}&height=${height}`;
    }, [response?.brandWatermarkId, response?.aspectRatio, brandId]);

    const [watermarkUrl, setWatermarkUrl] = useState<string | undefined>();
    const [isWatermarkReady, setIsWatermarkReady] = useState(false);

    useEffect(() => {
        if (!watermarkPath) {
            setWatermarkUrl(undefined);
            setIsWatermarkReady(true);
            return;
        }

        setIsWatermarkReady(false);
        setWatermarkUrl(undefined);

        let cancelled = false;
        let objectUrl: string | undefined;

        (async () => {
            try {
                const tokenRequest =
                    albumId !== null && albumId !== '' && albumId !== 'all'
                        ? { albumId }
                        : undefined;
                const token = await spAuthorizationToken.getAuthorizationTokenForEvent(
                    false,
                    tokenRequest
                );
                const res = await fetch(getBaseUrl() + watermarkPath, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!res.ok) {
                    if (!cancelled) {
                        setWatermarkUrl(undefined);
                        setIsWatermarkReady(true);
                    }

                    return;
                }

                const blob = await res.blob();

                if (!cancelled) {
                    objectUrl = URL.createObjectURL(blob);
                    setWatermarkUrl(objectUrl);
                    setIsWatermarkReady(true);
                }
            } catch {
                if (!cancelled) {
                    setWatermarkUrl(undefined);
                    setIsWatermarkReady(true);
                }
            }
        })();

        return () => {
            cancelled = true;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [watermarkPath, albumId, spAuthorizationToken]);

    // Loop: seek back to the beginning when the slideshow reaches its end
    useEffect(() => {
        if (totalDurationMs > 0 && currentTimeMs >= totalDurationMs) {
            tickerRef.current?.seek(0);
            setCurrentTimeMs(0);
        }
    }, [currentTimeMs, totalDurationMs]);

    const handlePlayPause = useCallback(() => {
        setIsPlaying((p) => !p);
        setHasStarted(true);
    }, []);

    const handleSeek = useCallback((timeSeconds: number) => {
        const timeMs = timeSeconds * 1000;
        tickerRef.current?.seek(timeMs);
        setCurrentTimeMs(timeMs);
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        if (document.fullscreenElement === containerRef.current) {
            document.exitFullscreen();
        } else if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
        }
    }, []);

    if (loading) {
        return (
            <Box
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <LoadingIcon width="64px" height="64px" />
            </Box>
        );
    }

    if (response?.type !== 'event-slideshow' || response.slides.length === 0) {
        return null;
    }

    const aspectRatio = response.aspectRatio ?? '16:9';
    const [arW, arH] = aspectRatio.split(':').map(Number);

    return (
        // Outer: fills the lightbox, centers the aspect-ratio-constrained inner box.
        // containerType: size establishes the container query context for the inner box.
        // No background here — the lightbox's own background shows in the areas not
        // covered by the player canvas, making different aspect ratios visually distinct.
        <Box
            ref={containerRef}
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="default"
            sx={{ containerType: 'size' }}
        >
            {/*
             * Pointer cursor + onClick: clicking the canvas toggles play/pause.
             * The controls' inner Flex calls e.stopPropagation(), so control-button
             * clicks never bubble up to this handler.
             */}
            <Box
                position="relative"
                cursor="pointer"
                onClick={hasStarted ? handlePlayPause : undefined}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    width: `min(100cqw, calc(100cqh * ${arW} / ${arH}))`,
                    height: `min(100cqh, calc(100cqw * ${arH} / ${arW}))`
                }}
            >
                {isWatermarkReady ? (
                    <SlideshowPlayerTicker
                        ref={tickerRef}
                        slideshow={response}
                        isPlaying={isPlaying}
                        onTimeChange={setCurrentTimeMs}
                        watermarkUrl={watermarkUrl}
                    />
                ) : (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <LoadingIcon width="80px" height="80px" />
                    </Box>
                )}

                {/* Big play circle — covers the canvas before first play.
                    Has its own onClick so there is no competing handler on the canvas Box. */}
                {!hasStarted && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        zIndex={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        onClick={handlePlayPause}
                    >
                        <Box
                            w="100px"
                            h="100px"
                            borderRadius="full"
                            bg="white"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            boxShadow="0px 10px 32px 0px rgba(0,0,0,0.14)"
                            pl="12px"
                            color="gray.500"
                        >
                            <PlayIcon width={64} height={64} />
                        </Box>
                    </Box>
                )}

                {/* Controls — visible on hover or when paused, only after first play */}
                {hasStarted && (isHovered || !isPlaying) && (
                    <SlideshowControls
                        isPlaying={isPlaying}
                        currentTime={currentTimeMs / 1000}
                        totalDuration={totalDurationMs / 1000}
                        isFullscreen={isFullscreen}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onToggleFullscreen={handleToggleFullscreen}
                    />
                )}
            </Box>
        </Box>
    );
};

export default ClientSlideshowViewer;
